import { formatCurrency } from "@theme/utilities";

const STORAGE_KEY = "theme:wishlist";
const CHANGE_EVENT = "wishlist:change";

/**
 * Reads the saved wishlist entries from localStorage.
 * Entries are stored as [{ id, handle }] so the wishlist page can
 * fetch full product data later without needing a server/app.
 * @returns {Array<{id: string, handle: string}>}
 */
function readEntries() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("Wishlist: could not read localStorage, starting empty.", error);
    return [];
  }
}

function writeEntries(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (error) {
    console.warn("Wishlist: could not save to localStorage (storage may be full or disabled).", error);
  }
}

/**
 * Central wishlist store. Single instance shared across the whole page
 * via `window.Wishlist`. Not tied to any one component so the header
 * icon, product-card hearts, the PDP button, and the wishlist page
 * all read/write the same source of truth.
 */
class WishlistStore {
  #entries;

  constructor() {
    this.#entries = readEntries();
  }

  /** @param {string|number} id */
  has(id) {
    const key = String(id);
    return this.#entries.some((entry) => entry.id === key);
  }

  get count() {
    return this.#entries.length;
  }

  get entries() {
    return [...this.#entries];
  }

  /**
   * @param {string|number} id
   * @param {string} handle
   */
  add(id, handle) {
    const key = String(id);
    if (this.has(key)) return;
    this.#entries.push({ id: key, handle: handle || "" });
    this.#persist();
  }

  /** @param {string|number} id */
  remove(id) {
    const key = String(id);
    const next = this.#entries.filter((entry) => entry.id !== key);
    if (next.length === this.#entries.length) return;
    this.#entries = next;
    this.#persist();
  }

  /**
   * @param {string|number} id
   * @param {string} handle
   * @returns {boolean} the new state (true = now saved, false = now removed)
   */
  toggle(id, handle) {
    if (this.has(id)) {
      this.remove(id);
      return false;
    }
    this.add(id, handle);
    return true;
  }

  #persist() {
    writeEntries(this.#entries);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { entries: this.entries } }));
  }
}

if (!window.Wishlist) {
  window.Wishlist = new WishlistStore();
}

/**
 * <wishlist-button> wraps a real <button> and syncs its pressed/active
 * state with the shared WishlistStore. Used on product cards and on
 * the product page. Two or more instances for the same product (e.g.
 * the same product shown in two carousels, or card + PDP open together)
 * all stay in sync via the wishlist:change event.
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle || "";
    this.labelAdd = this.dataset.labelAdd || "Add to wishlist";
    this.labelRemove = this.dataset.labelRemove || "Remove from wishlist";
    this.button = this.querySelector("button") || this;

    this.onClick = this.onClick.bind(this);
    this.onExternalChange = this.onExternalChange.bind(this);

    this.button.addEventListener("click", this.onClick);
    window.addEventListener(CHANGE_EVENT, this.onExternalChange);

    this.render();
  }

  disconnectedCallback() {
    this.button.removeEventListener("click", this.onClick);
    window.removeEventListener(CHANGE_EVENT, this.onExternalChange);
  }

  onClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.productId) return;

    const nowActive = window.Wishlist.toggle(this.productId, this.productHandle);
    this.render(nowActive);

    this.dispatchEvent(
      new CustomEvent("wishlist:toggle", {
        bubbles: true,
        detail: { id: this.productId, active: nowActive },
      })
    );
  }

  onExternalChange() {
    this.render();
  }

  render(forcedState) {
    const active = forcedState !== undefined ? forcedState : window.Wishlist.has(this.productId);
    this.classList.toggle("is-active", active);
    this.button.classList.toggle("is-active", active);
    this.button.setAttribute("aria-pressed", active ? "true" : "false");
    this.button.setAttribute("aria-label", active ? this.labelRemove : this.labelAdd);
  }
}

if (!customElements.get("wishlist-button")) {
  customElements.define("wishlist-button", WishlistButton);
}

/**
 * <wishlist-count> displays the current saved-item count, mirroring
 * the theme's existing <cart-count> pattern. Hides itself (and its
 * wrapper, if data-wishlist-count-wrapper is present on an ancestor)
 * when the count is zero.
 */
class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.update = this.update.bind(this);
    this.update();
    window.addEventListener(CHANGE_EVENT, this.update);
  }

  disconnectedCallback() {
    window.removeEventListener(CHANGE_EVENT, this.update);
  }

  update() {
    const count = window.Wishlist.count;
    this.textContent = count > 99 ? "99+" : String(count);
    this.classList.toggle("hidden", count === 0);
    const wrapper = this.closest("[data-wishlist-count-wrapper]");
    if (wrapper) wrapper.classList.toggle("hidden", count === 0);
  }
}

if (!customElements.get("wishlist-count")) {
  customElements.define("wishlist-count", WishlistCount);
}

/**
 * <wishlist-page> hydrates the dedicated wishlist page. Since Liquid
 * has no access to localStorage, this fetches each saved product's
 * public JSON (/products/{handle}.js) client-side and renders a
 * compact grid of cards. No app, no customer metafields required —
 * trade-off is this list is per-browser, not synced across devices.
 */
class WishlistPage extends HTMLElement {
  async connectedCallback() {
    this.grid = this.querySelector("[data-wishlist-grid]");
    this.emptyState = this.querySelector("[data-wishlist-empty]");
    this.loadingState = this.querySelector("[data-wishlist-loading]");

    window.addEventListener(CHANGE_EVENT, () => this.renderAll());
    await this.renderAll();
  }

  async renderAll() {
    const entries = window.Wishlist.entries;

    if (this.loadingState) this.loadingState.classList.add("hidden");

    if (entries.length === 0) {
      if (this.grid) this.grid.innerHTML = "";
      if (this.emptyState) this.emptyState.classList.remove("hidden");
      return;
    }

    if (this.emptyState) this.emptyState.classList.add("hidden");
    if (!this.grid) return;

    const products = await Promise.all(
      entries.map((entry) => this.fetchProduct(entry))
    );

    this.grid.innerHTML = products
      .filter(Boolean)
      .map((product) => this.renderCard(product))
      .join("");

    this.grid.querySelectorAll("[data-wishlist-remove]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        const id = button.dataset.wishlistRemove;
        window.Wishlist.remove(id);
      });
    });
  }

  /** @param {{id: string, handle: string}} entry */
  async fetchProduct(entry) {
    if (!entry.handle) return null;
    try {
      const response = await fetch(`/products/${entry.handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.warn("Wishlist: could not load product", entry.handle, error);
      return null;
    }
  }

  renderCard(product) {
    const image = product.featured_image || (product.images && product.images[0]) || "";
    const imageUrl = image ? image.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, "_400x.$1$2") : "";
    const price = formatCurrency(product.price);
    const compareAtPrice =
      product.compare_at_price && product.compare_at_price > product.price
        ? formatCurrency(product.compare_at_price)
        : "";

    return `
      <div class="wishlist-card">
        <button
          type="button"
          class="wishlist-card__remove"
          data-wishlist-remove="${product.id}"
          aria-label="Remove from wishlist"
        >
          ${HEART_FILLED_SVG}
        </button>
        <a href="${product.url}" class="wishlist-card__media">
          ${
            imageUrl
              ? `<img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy" width="400" height="400">`
              : ""
          }
        </a>
        <div class="wishlist-card__content">
          <a href="${product.url}" class="wishlist-card__title">${escapeHtml(product.title)}</a>
          <div class="wishlist-card__price">
            <span class="wishlist-card__price-current">${price}</span>
            ${
              compareAtPrice
                ? `<span class="wishlist-card__price-compare">${compareAtPrice}</span>`
                : ""
            }
          </div>
          <a href="${product.url}" class="btn btn--primary w-full wishlist-card__cta">
            <span class="btn__text">${product.available ? "View product" : "Sold out"}</span>
          </a>
        </div>
      </div>
    `;
  }
}

const HEART_FILLED_SVG =
  '<svg viewBox="0 0 22 22" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M11 19.3 2.6 11c-2-2-2-5.4 0-7.4 2-2.1 5.4-2.1 7.4 0l1 1 1-1c2-2.1 5.4-2.1 7.4 0 2 2 2 5.4 0 7.4L11 19.3Z"/></svg>';

function escapeHtml(value) {
  const div = document.createElement("div");
  div.textContent = value ?? "";
  return div.innerHTML;
}

if (!customElements.get("wishlist-page")) {
  customElements.define("wishlist-page", WishlistPage);
}
